import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isVideoUrl } from '../../Services/ApiService';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import SystemDataService from '../../Services/SystemDataService';
import VideoComponent from '../Video/video-component';
import Image from '../../Utils/Image/image-component';
import { ButtonSecondary, ButtonTransparent } from '../Buttons/button-component';
import useScrollOrNavigate from '../../Hooks/useScrollOrNavigate';

export default function HeroSection({ heroContent }) {
    const scrollOrNavigate = useScrollOrNavigate();
    const systemData = useSelector((state) => state.systemData || {});
    const { dir } = systemData;

    const [carouselImages, setCarouselImages] = useState([]);

    const {
        heroTitle,
        heroTagline,
        heroDescription,
        heroMedia,
        primaryCtaText,
        secondaryCtaText
    } = heroContent || {};

    useEffect(() => {
        async function fetchCarousel() {
            try {
                const images = await SystemDataService.getCarouselImages();
                if (images && images.length > 0) {
                    setCarouselImages(images.map(img => img.image));
                }
            } catch (err) {
                console.error("Failed to load carousel images", err);
            }
        }
        fetchCarousel();
    }, []);

    // Use heroMedia for the background, fallback to carousel
    const finalHeroMedia = heroMedia || carouselImages[0];
    const isHeroVideo = isVideoUrl(finalHeroMedia);

    /**
     * Custom Title Renderer for BH-Hardware Mockup Layout
     * Example Input: "PROFESSIONAL GARAGE DOOR HARDWARE. SHIPPED FAST."
     * Group 1: PROFESSIONAL (White)
     * Group 2: GARAGE DOOR (Red)
     * Group 3: HARDWARE. (White)
     * Remainder: SHIPPED FAST. (White with red bottom bar)
     */
    const renderTitle = (text) => {
        if (!text) return null;
        const words = text.split(' ');

        const word1 = words[0] || '';
        const word2_3 = words.slice(1, 3).join(' ');
        const word4 = words[3] || '';
        const remainder = words.slice(4).join(' ');

        return (
            <>
                <span className="text-white">{word1}</span>{' '}
                <span className="text-secondary-500">{word2_3}</span>{' '}
                <span className="text-white">{word4}</span>
                {remainder && (
                    <>
                        <br />
                        <span className="text-white relative inline-block mt-4 pb-2">
                            {remainder}
                            {/* Thick Red underline effect matching the mockup */}
                            <span className="absolute left-0 bottom-0 w-[45%] h-2.5 bg-secondary-500"></span>
                        </span>
                    </>
                )}
            </>
        );
    };

    return (
        <div key={dir} dir={dir} className="relative w-full flex flex-col items-center bg-black overflow-hidden">

            <div className="relative w-full flex justify-center min-h-[800px] lg:h-[85vh] xl:h-[90vh]">

                {/* --- FULL WIDTH BACKGROUND MEDIA LAYER --- */}
                <div className="absolute inset-0 w-full h-full z-0">
                    {finalHeroMedia && (
                        isHeroVideo ? (
                            <VideoComponent
                                video={finalHeroMedia}
                                mobile={true}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <Image
                                src={finalHeroMedia}
                                quality={90}
                                alt="Warehouse Background"
                                className="w-full h-full object-cover object-center"
                                wrapperClassName="w-full h-full"
                            />
                        )
                    )}
                </div>

                {/* Gradient overlay — black at top fading to transparent */}
                <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-b from-black via-black/50 to-transparent" />

                {/* --- CONTENT LAYER --- */}
                <div className="relative z-20 w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 flex items-center pt-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full">

                        {/* Left Side: Text & Buttons */}
                        <div className={`col-span-1 flex flex-col space-y-6 ${dir === 'rtl' ? 'text-right items-start' : 'text-left items-start'}`}>

                            {/* Small Red Badge */}
                            {heroTagline && (
                                <div className="bg-secondary-500 text-white uppercase text-sm md:text-base font-bold tracking-widest py-1 px-4 rounded-sm shadow-md">
                                    {heroTagline}
                                </div>
                            )}

                            {/* Main Title */}
                            <div className="space-y-4">
                                {heroTitle && (
                                    <h1 className="text-4xl md:text-7xl lg:text-[5.5rem] font-bold leading-[0.95]  uppercase">
                                        {renderTitle(heroTitle)}
                                    </h1>
                                )}
                            </div>

                            {/* Description */}
                            <div className="text-base md:text-xl lg:text-xl text-gray-200 font-medium max-w-2xl leading-relaxed mt-6">
                                {heroDescription}
                            </div>
                            {/* Action Buttons using Global Components */}
                            <div className="flex flex-col sm:flex-row items-center gap-5 pt-8 w-full sm:max-w-2xl">

                                {primaryCtaText && (
                                    <ButtonSecondary
                                        onClick={scrollOrNavigate}
                                        size="lg"
                                        icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>}
                                    >
                                        {primaryCtaText}

                                    </ButtonSecondary>
                                )}

                                {secondaryCtaText && (
                                    <ButtonTransparent
                                        size="lg"
                                        onClick={scrollOrNavigate}
                                    >
                                        {secondaryCtaText}
                                    </ButtonTransparent>
                                )}

                            </div>
                        </div>

                        {/* Right Side: 3D Badge Logo */}
                        {/* <div className="col-span-1 hidden lg:flex justify-end items-center relative h-[600px] xl:h-[700px]">
                            <div
                                className="w-[85%] max-w-[700px] transition-transform duration-700 ease-out hover:scale-105"
                                style={{ transform: 'perspective(1200px) rotateY(-18deg) rotateX(8deg)' }}
                            >
                                <img
                                    src="https://sawpy-assets.s3.us-east-2.amazonaws.com/BH-Hardware/logos/BH_Logo_Badge.png"
                                    alt="BH Hardware Badge"
                                    className="w-full h-auto object-contain filter drop-shadow-[0_30px_50px_rgba(0,0,0,0.6)]"
                                />
                            </div>
                        </div> */}

                    </div>
                </div>
            </div>
        </div>
    );
}